/**
 * Red Flags — Differential Intelligence Panel™ + Reverse Differential Engine™.
 *
 * Two modes:
 *   • Forward — clinician names a diagnosis → can't-miss differentials,
 *               red flags, why-urgency, lifecycle.
 *   • Reverse — clinician inputs raw findings (symptoms / vitals / labs /
 *               psychosocial) → ranked most-likely, can't-miss rule-outs,
 *               patho chain, lifecycle progression, SDoH accelerators.
 *
 * Both routes are advisory only (Maldek OS v1.1 § 5).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import DiseaseAutocomplete from "../components/DiseaseAutocomplete";

export default function RedFlags() {
  const [mode, setMode] = useState("forward");

  return (
    <div className="bg-background min-h-screen" data-testid="red-flags-page">
      <section className="bg-white/[0.04] border-b border-white/8">
        <div className="max-w-5xl mx-auto px-6 py-14 sm:py-20">
          <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D96C5B] mb-3">
            Maldek · Differential Intelligence Panel™
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[1.05]">
            What kills this patient first?
            <br />
            <span className="italic font-normal text-[#6B8F71]">
              Reasoning, not memorization.
            </span>
          </h1>
          <p className="mt-5 text-base text-white/65 max-w-2xl leading-relaxed">
            Forward mode: name a diagnosis → red flags, can't-miss
            alternatives, mortality clock. Reverse mode: drop in symptoms /
            vitals / labs → ranked differentials + can't-miss rule-outs +
            pathophysiology chain. Advisory only — a clinician confirms.
          </p>

          <div
            className="mt-7 inline-flex glass-card rounded-full p-1"
            data-testid="rf-mode-toggle"
          >
            <button
              type="button"
              onClick={() => setMode("forward")}
              data-testid="rf-mode-forward"
              className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                mode === "forward" ? "chip-active text-white" : "text-white/55 hover:text-white"
              }`}
            >
              Forward · Diagnosis → red flags
            </button>
            <button
              type="button"
              onClick={() => setMode("reverse")}
              data-testid="rf-mode-reverse"
              className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest transition-colors ${
                mode === "reverse" ? "chip-active text-white" : "text-white/55 hover:text-white"
              }`}
            >
              Reverse · Findings → differential
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          {mode === "forward" ? (
            <motion.div
              key="forward"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              <ForwardMode />
            </motion.div>
          ) : (
            <motion.div
              key="reverse"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              <ReverseMode />
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}

/* Named exports so other pages can compose these modes inline. */
export { ForwardMode, ReverseMode };

/* ============================ FORWARD MODE ============================ */
function ForwardMode({ isCommunity = false }) {
  const [dx, setDx] = useState("");
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Suggestion chips. Clinicians get the must-not-miss high-acuity list;
  // community gets everyday plain-English conditions a patient might be
  // told they have at a visit.
  const SUGGESTIONS = isCommunity
    ? [
        "High blood pressure",
        "Type 2 diabetes",
        "Asthma",
        "Heart failure",
        "Depression",
        "UTI (urinary tract infection)",
        "Pneumonia",
        "Migraine",
        "Anxiety",
        "GERD (acid reflux)",
      ]
    : [
        "Pulmonary embolism",
        "Septic shock",
        "Diabetic ketoacidosis",
        "Aortic dissection",
        "Subarachnoid hemorrhage",
        "Acute MI (STEMI)",
        "Necrotizing fasciitis",
        "Anaphylaxis",
        "Status epilepticus",
        "Hyperkalemia",
      ];

  async function submit(e) {
    e?.preventDefault();
    if (!dx.trim()) return;
    setLoading(true);
    setErr("");
    setPanel(null);
    try {
      const r = await api.post("/red-flags/forward", {
        diagnosis: dx.trim(),
        level: isCommunity ? "community" : "clinician",
      });
      setPanel(r.data.panel);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't generate panel.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* How-to directions for Forward mode. Adapts to audience. */}
      <div
        data-testid="rf-fwd-howto"
        className="mb-5 rounded-2xl border border-[#6B8F71]/25 p-4 sm:p-5"
        style={{ background: "rgba(107, 143, 113, 0.06)" }}
      >
        <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#6B8F71] font-semibold mb-2">
          How to use Forward · {isCommunity ? "diagnosis → what to watch" : "diagnosis → red flags"}
        </p>
        <p className="text-sm text-white/85 leading-relaxed mb-3">
          {isCommunity
            ? "Start from the name your provider gave you. The engine tells you what to watch for at home, which symptoms mean call the doctor, and which mean go to the ER right now."
            : "Name the diagnosis. The engine returns its can't-miss alternatives, the mortality clock, and the time-critical next step."}
        </p>
        <ol className="space-y-1.5 text-[13px] text-white/75 list-none">
          <li className="flex gap-2">
            <span className="font-mono text-[11px] text-[#6B8F71] shrink-0 w-5">1.</span>
            <span>
              {isCommunity
                ? "Type the condition you were told you have (or tap one of the chips below)."
                : "Type a working diagnosis — or tap a chip for a high-acuity must-not-miss."}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="font-mono text-[11px] text-[#6B8F71] shrink-0 w-5">2.</span>
            <span>
              Tap <span className="font-mono text-white">Build panel →</span>.
              {" "}
              {isCommunity
                ? "You'll get a plain-English red-flag list: what to watch, when to call, when to go."
                : "You'll get the differential, the can't-miss diagnoses you must rule out, and a mortality clock."}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="font-mono text-[11px] text-[#6B8F71] shrink-0 w-5">3.</span>
            <span>
              {isCommunity
                ? "Cross-check with the Reverse tab if your symptoms don't seem to fit — it works backwards from what you're feeling."
                : "Flip to the Reverse tab to rebuild the panel from raw findings if the picture doesn't match."}
            </span>
          </li>
        </ol>
        <p className="text-[11px] text-white/55 mt-3 italic">
          {isCommunity
            ? "Tip: any wording works — even if you remember the name a little differently. The engine will recognize it."
            : "Tip: synonyms and abbreviations both work — the engine normalizes diagnoses on its own."}
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-wrap gap-2 mb-3 items-stretch">
        <div className="flex-1 min-w-[260px]">
          <DiseaseAutocomplete
            value={dx}
            onChange={setDx}
            placeholder={isCommunity
              ? "Search any condition — start typing what your provider told you"
              : "Search any condition — e.g. pulmonary embolism, DKA"}
            testid="rf-fwd-input"
            className="border-white/8 focus:border-[#D96C5B]/50 py-3"
            limit={10}
          />
        </div>
        <button
          type="submit"
          disabled={loading || !dx.trim()}
          data-testid="rf-fwd-submit"
          className="bg-[#D49191]/20 hover:bg-[#D49191]/30 disabled:opacity-50 text-white px-5 py-3 rounded-lg text-sm font-medium"
        >
          {loading ? "Generating…" : "Build panel →"}
        </button>
      </form>

      <div className="mb-8" data-testid="rf-fwd-suggestions">
        <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-foreground/45 mb-2">
          Try one
        </p>
        <div className="flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => setDx(s)}
              data-testid={`rf-fwd-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="text-[11px] glass-chip px-3 py-1 text-foreground/75 hover:text-foreground"
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {err && <p className="text-sm text-[#D96C5B] mb-4">{err}</p>}

      {panel && <ForwardPanel panel={panel} />}
    </>
  );
}

function ForwardPanel({ panel }) {
  const conf =
    typeof panel.confidence_score === "number"
      ? Math.round(panel.confidence_score * 100)
      : null;

  return (
    <div className="space-y-8" data-testid="rf-fwd-panel">
      <PanelHeader title={panel.diagnosis} conf={conf} unc={panel.uncertainty_note} />

      {/* The body's story — flowing prose that frames every panel below.
          Reads conversationally so newcomers + experts both land softly
          before stepping into the structured differentials. */}
      {panel.disease_overview && (
        <Block label="The story behind it" testid="rf-fwd-overview">
          <p
            className="text-foreground/85 leading-relaxed text-[15px] whitespace-pre-wrap"
            data-testid="rf-fwd-overview-text"
          >
            {panel.disease_overview}
          </p>
        </Block>
      )}

      <Block label="High-yield differentials" testid="rf-fwd-differentials">
        {(panel.high_yield_differentials || []).map((d, i) => (
          <Card key={i}>
            <Strong>{d.dx}</Strong>
            <Small>{d.why_consider}</Small>
          </Card>
        ))}
      </Block>

      <Block label="Dangerous alternatives" tone="warn" testid="rf-fwd-dangerous">
        {(panel.dangerous_alternatives || []).map((d, i) => (
          <Card key={i} tone="warn">
            <Strong tone="warn">{d.dx}</Strong>
            <Small>{d.why_dangerous}</Small>
          </Card>
        ))}
      </Block>

      <Block label="Red flags · what they imply" tone="danger" testid="rf-fwd-redflags">
        {(panel.red_flags || []).map((r, i) => (
          <Card key={i} tone="danger">
            <Strong tone="danger">{r.finding}</Strong>
            <Small>→ {r.implies}</Small>
          </Card>
        ))}
      </Block>

      {panel.why_urgency && (
        <Block label="Why the urgency?" testid="rf-fwd-urgency">
          <Kv k="Mortality window" v={panel.why_urgency.mortality_window} />
          <Kv k="Patho clock" v={panel.why_urgency.pathophysiology_clock} />
          <Kv k="Treatment window" v={panel.why_urgency.treatment_window} />
          <Kv
            k="What inexperienced clinicians miss"
            v={panel.why_urgency.what_inexperienced_clinicians_miss}
            tone="warn"
          />
        </Block>
      )}

      {Array.isArray(panel.lifecycle_map) && panel.lifecycle_map.length > 0 && (
        <Block label="Lifecycle map · intervention points" testid="rf-fwd-lifecycle">
          <ol className="space-y-3">
            {panel.lifecycle_map.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B8F71] shrink-0 mt-1">
                  {s.stage}
                </span>
                <div>
                  <p className="text-sm text-white/85">{s.complication}</p>
                  <p className="text-xs text-[#6B8F71] mt-0.5">
                    <span className="font-mono uppercase tracking-widest text-[10px] mr-1.5">
                      Intervene
                    </span>
                    {s.intervention_point}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Block>
      )}

      <References refs={panel.references} />
    </div>
  );
}

/* ============================ REVERSE MODE ============================ */
function ReverseMode({ isCommunity = false }) {
  const [findings, setFindings] = useState([""]);
  const [panel, setPanel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Quick-add chips — grouped by what kind of signal it is. Clinicians
  // get crisp vitals + lab values; community gets plain-English everyday
  // body cues + life-context items.
  const QUICK_FINDINGS = isCommunity
    ? [
        { group: "Body", items: [
          "Chest hurts when I breathe",
          "Can't catch my breath",
          "Pounding heart",
          "Swollen ankles by night",
          "Peeing 4× a night",
        ] },
        { group: "Head", items: [
          "Worst headache of my life",
          "Sudden one-sided weakness",
          "Slurred speech for an hour",
          "Vision went blurry",
          "Felt confused, then better",
        ] },
        { group: "Belly", items: [
          "Sharp stomach pain",
          "Vomiting can't keep water down",
          "Black or tarry stool",
          "Yellow eyes / skin",
        ] },
        { group: "Life", items: [
          "Skipped meds 3 days",
          "Lost my job last month",
          "Out of food this week",
          "Couldn't make my dialysis ride",
          "Caregiver burnout",
        ] },
      ]
    : [
        { group: "Vitals", items: ["SBP 84", "HR 132", "RR 28", "T 39.4°C", "SpO2 87% RA"] },
        { group: "Labs", items: ["Lactate 4.2", "Cr jumped 1.1→2.6", "K+ 6.4", "Glucose 480", "Trop 0.42"] },
        { group: "Symptoms", items: ["Sudden chest pain", "Tearing back pain", "Worst headache of life", "One-sided weakness", "Pleuritic SOB"] },
        { group: "SDoH", items: ["Lives in shelter", "Missed last 2 dialysis", "Out of insulin x3 days", "Recent IV drug use", "Caregiver burnout"] },
      ];

  function addFinding(text) {
    setFindings((arr) => {
      const idx = arr.findIndex((x) => !x.trim());
      if (idx >= 0) {
        return arr.map((x, j) => (j === idx ? text : x));
      }
      return [...arr, text];
    });
  }

  async function submit() {
    const cleaned = findings.map((f) => f.trim()).filter(Boolean);
    if (cleaned.length === 0) return;
    setLoading(true);
    setErr("");
    setPanel(null);
    try {
      const r = await api.post("/red-flags/reverse", {
        findings: cleaned,
        level: isCommunity ? "community" : "clinician",
      });
      setPanel(r.data.panel);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Couldn't generate panel.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {/* How-to directions — explicit so the user knows what to do.
          Plain-English for community, clinical voice for clinicians. */}
      <div
        data-testid="rf-rev-howto"
        className="mb-5 rounded-2xl border border-[#6B8F71]/25 p-4 sm:p-5"
        style={{ background: "rgba(107, 143, 113, 0.06)" }}
      >
        <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#6B8F71] font-semibold mb-2">
          How to use Symptoms · {isCommunity ? "what's bothering you" : "findings → differential"}
        </p>
        <p className="text-sm text-white/85 leading-relaxed mb-3">
          {isCommunity
            ? "Tell us what you're noticing. The engine will list possible causes, ranked, and tell you which ones need help right away."
            : "Drop in raw findings — vitals, labs, symptoms, even SDoH. The engine returns a ranked differential with the can't-miss diagnoses flagged."}
        </p>
        <ol className="space-y-1.5 text-[13px] text-white/75 list-none">
          <li className="flex gap-2">
            <span className="font-mono text-[11px] text-[#6B8F71] shrink-0 w-5">1.</span>
            <span>
              {isCommunity
                ? "Type one concern in each box (or tap a chip below to add it quickly)."
                : "Type one finding per row, or tap a chip below — vitals, labs, symptoms, or SDoH."}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="font-mono text-[11px] text-[#6B8F71] shrink-0 w-5">2.</span>
            <span>
              Tap <span className="font-mono text-white">+ Add finding</span>{" "}
              to enter more. {isCommunity
                ? "Three or four is usually enough."
                : "More signals = sharper ranking, but 4–8 is the sweet spot."}
            </span>
          </li>
          <li className="flex gap-2">
            <span className="font-mono text-[11px] text-[#6B8F71] shrink-0 w-5">3.</span>
            <span>
              Hit <span className="font-mono text-white">Generate differential</span>.
              {" "}
              {isCommunity
                ? "You'll get a ranked list of possible causes plus a red-flag warning for anything that means call 911 or go to the ER."
                : "You'll get a ranked DDx, mortality clocks on the leading diagnosis, and the next decision step."}
            </span>
          </li>
        </ol>
        <p className="text-[11px] text-white/55 mt-3 italic">
          {isCommunity
            ? "Tip: any wording works — describe it however makes sense to you. The engine reads plain English."
            : "Tip: any phrasing works — abbreviations, units optional. The engine normalizes findings on its own."}
        </p>
      </div>

      <div className="space-y-2 mb-4">
        {findings.map((f, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={f}
              onChange={(e) =>
                setFindings((arr) => arr.map((x, j) => (i === j ? e.target.value : x)))
              }
              placeholder={isCommunity
                ? `Concern ${i + 1} — e.g. "swollen ankles", "can't catch my breath", "missed 3 days of meds"`
                : `Finding ${i + 1} — e.g. "SBP 84", "lactate 4.2", "lives in shelter", "missed last 2 dialysis"`}
              data-testid={`rf-rev-finding-${i}`}
              className="flex-1 border border-white/8 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#D96C5B]/50"
            />
            {findings.length > 1 && (
              <button
                type="button"
                onClick={() => setFindings((arr) => arr.filter((_, j) => j !== i))}
                className="text-white/45 hover:text-[#D49191] text-xs font-mono uppercase tracking-widest px-2"
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>

      <div className="mb-5" data-testid="rf-rev-suggestions">
        <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-foreground/45 mb-2">
          Quick-add findings
        </p>
        <div className="space-y-2">
          {QUICK_FINDINGS.map((grp) => (
            <div key={grp.group} className="flex flex-wrap items-baseline gap-1.5">
              <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/40 w-24 shrink-0">
                {grp.group}
              </span>
              {grp.items.map((s) => (
                <button
                  type="button"
                  key={s}
                  onClick={() => addFinding(s)}
                  data-testid={`rf-rev-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="text-[11px] glass-chip px-3 py-1 text-foreground/75 hover:text-foreground"
                >
                  + {s}
                </button>
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2 mb-8">
        <button
          type="button"
          onClick={() => setFindings((arr) => [...arr, ""])}
          data-testid="rf-rev-add"
          className="text-xs font-mono uppercase tracking-widest text-[#6B8F71] hover:text-[#6B8F71]"
        >
          + Add finding
        </button>
        <div className="flex-1" />
        <button
          type="button"
          onClick={submit}
          disabled={loading}
          data-testid="rf-rev-submit"
          className="bg-[#D96C5B]/20 hover:bg-[#D96C5B]/30 disabled:opacity-50 text-white px-5 py-2.5 rounded-lg text-sm font-medium"
        >
          {loading ? "Reasoning…" : "Generate differential →"}
        </button>
      </div>

      {err && <p className="text-sm text-[#D96C5B] mb-4">{err}</p>}

      {/* LIVE SYNTHESIS FALLBACK — when the user typed exactly ONE
          finding, route them to the on-demand symptom workup. The
          symptom synthesis returns common causes, concerning causes,
          self-check questions, ER red flags, and likely next steps —
          a deeper plain-English walkthrough than the differential
          panel offers for a single symptom. Shown alongside the
          panel so both paths are available. */}
      {!loading && findings.filter((f) => f.trim()).length === 1 && (
        <div
          className="mb-4 rounded-2xl border border-[#6B8F71]/35 p-5"
          style={{
            background:
              "linear-gradient(135deg, rgba(70, 117, 184, 0.20), rgba(31, 49, 96, 0.55))",
          }}
          data-testid="rev-live-synth-fallback"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#6B8F71] mb-1">
                ⚡ Deep-dive this symptom
              </p>
              <p className="text-white font-heading text-lg leading-tight">
                "{findings.find((f) => f.trim())?.trim()}"
              </p>
              <p className="text-sm text-white/65 mt-1 leading-relaxed">
                Our medical-intelligence engine writes a full structured
                symptom workup — common causes, what's concerning, self-
                check questions, ER red flags, likely next steps. Plain
                English, cached 30 days.
              </p>
            </div>
            <Link
              to={`/synth/symptom?name=${encodeURIComponent(findings.find((f) => f.trim())?.trim())}&audience=${isCommunity ? "community" : "clinician"}`}
              data-testid="rev-go-synth"
              className="shrink-0 bg-[#6B8F71] hover:bg-[#6B8F71] text-white px-5 py-2.5 rounded-full text-sm font-medium transition-colors"
            >
              Open live workup →
            </Link>
          </div>
        </div>
      )}

      {panel && <ReversePanel panel={panel} />}
    </>
  );
}

function ReversePanel({ panel }) {
  const conf =
    typeof panel.confidence_score === "number"
      ? Math.round(panel.confidence_score * 100)
      : null;

  const sevTone = (s) =>
    s === "critical" ? "danger" : s === "high" ? "warn" : "info";

  return (
    <div className="space-y-8" data-testid="rf-rev-panel">
      <PanelHeader
        title="Reverse differential"
        conf={conf}
        unc={panel.uncertainty_note}
      />

      {/* The body's story for the submitted finding cluster — flowing
          prose explaining the WHY behind each symptom before the
          structured differentials. */}
      {panel.findings_overview && (
        <Block label="The story behind these findings" testid="rf-rev-overview">
          <p
            className="text-foreground/85 leading-relaxed text-[15px] whitespace-pre-wrap"
            data-testid="rf-rev-overview-text"
          >
            {panel.findings_overview}
          </p>
        </Block>
      )}

      <Block label="Most likely differentials" testid="rf-rev-most-likely">
        {(panel.most_likely_differentials || []).map((d, i) => (
          <Card key={i} tone={sevTone(d.severity)}>
            <div className="flex items-baseline gap-2 flex-wrap">
              <Strong tone={sevTone(d.severity)}>{d.dx}</Strong>
              <span className="text-[10px] font-mono uppercase tracking-widest text-white/55">
                · {d.probability_qualitative} · {d.severity} · {d.time_sensitivity}
              </span>
            </div>
            <Small>{d.why_it_fits}</Small>
          </Card>
        ))}
      </Block>

      <Block label="Can't-miss rule-outs" tone="danger" testid="rf-rev-cant-miss">
        {(panel.cant_miss_rule_outs || []).map((d, i) => (
          <Card key={i} tone="danger">
            <Strong tone="danger">{d.dx}</Strong>
            <Small>
              <span className="font-mono uppercase tracking-widest text-[10px] text-[#D96C5B] mr-1.5">
                Clock
              </span>
              {d.deterioration_clock}
            </Small>
            <Small>
              <span className="font-mono uppercase tracking-widest text-[10px] text-[#D96C5B] mr-1.5">
                If missed
              </span>
              {d.irreversible_endpoint}
            </Small>
            <Small>
              <span className="font-mono uppercase tracking-widest text-[10px] text-[#6B8F71] mr-1.5">
                First step
              </span>
              {d.first_step_to_exclude}
            </Small>
          </Card>
        ))}
      </Block>

      {Array.isArray(panel.pathophysiology_chain) && panel.pathophysiology_chain.length > 0 && (
        <Block label="Pathophysiology chain" testid="rf-rev-chain">
          {panel.pathophysiology_chain.map((p, i) => (
            <Card key={i}>
              <Strong>{p.finding}</Strong>
              <Small>→ {p.physiologic_link}</Small>
              <p className="text-[10px] font-mono uppercase tracking-widest text-[#6B8F71] mt-1">
                {p.system}
              </p>
            </Card>
          ))}
        </Block>
      )}

      {Array.isArray(panel.lifecycle_progression) && panel.lifecycle_progression.length > 0 && (
        <Block label="Lifecycle progression · what's next" testid="rf-rev-progression">
          <ol className="space-y-3">
            {panel.lifecycle_progression.map((s, i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#6B8F71] shrink-0 mt-1">
                  {s.horizon}
                </span>
                <div>
                  <p className="text-sm text-white/85">{s.expected_complication}</p>
                  <p className="text-xs text-[#6B8F71] mt-0.5">
                    → {s.preventable_intervention}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </Block>
      )}

      {Array.isArray(panel.social_determinants_accelerating) && panel.social_determinants_accelerating.length > 0 && (
        <Block label="Social determinants accelerating decline" testid="rf-rev-sdoh">
          {panel.social_determinants_accelerating.map((s, i) => (
            <Card key={i} tone="amber">
              <Strong tone="amber">{s.determinant}</Strong>
              <Small>{s.biological_pathway}</Small>
            </Card>
          ))}
        </Block>
      )}

      {Array.isArray(panel.next_step_workup) && panel.next_step_workup.length > 0 && (
        <Block label="Next-step workup · in priority order" testid="rf-rev-workup">
          <ol className="list-decimal list-inside space-y-1 text-sm text-white/85">
            {panel.next_step_workup.map((s, i) => <li key={i}>{s}</li>)}
          </ol>
        </Block>
      )}

      {panel.escalation_recommendation && (
        <Block label="Escalation recommendation" tone="warn">
          <p className="text-sm text-white/85">{panel.escalation_recommendation}</p>
        </Block>
      )}

      {panel.clinician_action_required && (
        <div
          className="glass-card-soft border border-white/15 rounded-2xl p-4"
          data-testid="rf-rev-clinician-required"
        >
          <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#6B8F71] mb-1.5">
            Clinician action required
          </p>
          <p className="text-sm text-white/90">{panel.clinician_action_required}</p>
        </div>
      )}
    </div>
  );
}

/* ============================ SHARED UI ============================ */

function PanelHeader({ title, conf, unc }) {
  return (
    <div className="glass-card-soft border border-[#D96C5B]/40 rounded-2xl p-5">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-1.5">
        <p className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#D96C5B]">
          {title}
        </p>
        {conf !== null && (
          <span
            className="text-[10px] font-mono uppercase tracking-widest"
            data-testid="rf-confidence"
            title="Confidence in the underlying public-health / peer-reviewed evidence."
          >
            <span className="text-white/55 mr-2">Evidence confidence</span>
            <span
              className={
                conf >= 80
                  ? "text-[#6B8F71]"
                  : conf >= 60
                  ? "text-[#6B8F71]"
                  : "text-[#D96C5B]"
              }
            >
              {conf}%
            </span>
          </span>
        )}
      </div>
      {unc && (
        <p className="text-xs text-[#6B8F71]/90 leading-relaxed mt-2" data-testid="rf-uncertainty">
          <span className="font-mono uppercase tracking-[0.22em] text-[10px] mr-2">
            Uncertainty
          </span>
          {unc}
        </p>
      )}
      <p className="mt-2 text-[10px] text-white/45 italic">
        ⚠ Advisory only — Maldek OS v1.1. A licensed clinician must confirm
        before any patient-level decision.
      </p>
    </div>
  );
}

const TONE_BORDER = {
  info: "border-white/8",
  warn: "border-white/15",
  danger: "border-[#D96C5B]/50",
  amber: "border-white/15",
};

const TONE_BG = {
  info: "glass-card",
  warn: "bg-white/10/8",
  danger: "bg-[#D96C5B]/10",
  amber: "bg-white/10/8",
};

function Block({ label, tone = "info", children, testid }) {
  const color =
    tone === "danger" ? "text-[#D96C5B]" : tone === "warn" || tone === "amber" ? "text-[#6B8F71]" : "text-[#6B8F71]";
  return (
    <section data-testid={testid}>
      <p className={`text-[10px] font-mono uppercase tracking-[0.28em] ${color} mb-3`}>
        {label}
      </p>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

function Card({ tone = "info", children }) {
  return (
    <div className={`rounded-xl p-4 border ${TONE_BG[tone]} ${TONE_BORDER[tone]}`}>
      {children}
    </div>
  );
}

function Strong({ tone = "info", children }) {
  const color =
    tone === "danger" ? "text-[#D96C5B]" : tone === "warn" || tone === "amber" ? "text-[#6B8F71]" : "text-white";
  return <p className={`font-medium ${color}`}>{children}</p>;
}

function Small({ children }) {
  return <p className="text-xs text-white/75 mt-1 leading-relaxed">{children}</p>;
}

function Kv({ k, v, tone = "info" }) {
  const color =
    tone === "warn" ? "text-[#6B8F71]" : tone === "danger" ? "text-[#D96C5B]" : "text-white/55";
  return (
    <p className="text-sm mb-1.5">
      <span className={`font-mono uppercase tracking-widest text-[10px] mr-2 ${color}`}>
        {k}
      </span>
      <span className="text-white/85">{v}</span>
    </p>
  );
}

function References({ refs }) {
  if (!Array.isArray(refs) || refs.length === 0) return null;
  return (
    <section data-testid="rf-references">
      <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#6B8F71] mb-3">
        References
      </p>
      <ol className="space-y-2">
        {refs.map((r) => (
          <li key={r.n} className="text-xs text-white/75 flex gap-2">
            <span className="font-mono text-[#6B8F71] shrink-0">[{r.n}]</span>
            {r.url ? (
              <a
                href={r.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#6B8F71] hover:text-[#6B8F71] break-all"
              >
                {r.label || r.url}
              </a>
            ) : (
              <span>{r.label}</span>
            )}
          </li>
        ))}
      </ol>
    </section>
  );
}
